import React from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import Tooltip from "@mui/material/Tooltip";
import { formatDistance } from "date-fns";
import { getMapMetaById, getMapImageUrl } from "../../constants/mapMeta";

function getPlayerInitial(name) {
  const trimmed = (name || "").trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
}

function formatRosterNames(players) {
  if (!Array.isArray(players) || players.length === 0) return "No players yet";
  return players.map((player) => player?.name || "Unknown").join(", ");
}

function TeamAvatars({ players, side }) {
  if (!Array.isArray(players) || players.length === 0) return null;

  return (
    <AvatarGroup
      max={5}
      sx={{
        flexDirection: side === "left" ? "row-reverse" : "row",
        "& .MuiAvatar-root": {
          width: 26,
          height: 26,
          fontSize: "0.7rem",
          fontWeight: 700,
          border: "2px solid #171717",
          backgroundColor: "rgba(232, 163, 61, 0.18)",
          color: "#E8A33D",
        },
      }}
    >
      {players.map((player, index) => (
        <Tooltip
          key={player._id || player.userId || player.steamId || index}
          title={player.name || "Unknown"}
        >
          <Avatar src={player.avatarUrl || undefined} alt={player.name || "Player"}>
            {getPlayerInitial(player.name)}
          </Avatar>
        </Tooltip>
      ))}
    </AvatarGroup>
  );
}

TeamAvatars.propTypes = {
  players: PropTypes.array,
  side: PropTypes.oneOf(["left", "right"]),
};

/**
 * One side of the head-to-head row. Content hugs the centered score:
 * left team = [names][avatars], right team = [avatars][names].
 */
function TeamSide({ players, side }) {
  const rosterNames = formatRosterNames(players);

  const avatars = <TeamAvatars players={players} side={side} />;
  const names = (
    <Typography
      title={rosterNames}
      sx={{
        fontSize: 13,
        color: "#cfcfcf",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        textAlign: side === "left" ? "right" : "left",
      }}
    >
      {rosterNames}
    </Typography>
  );

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: side === "left" ? "flex-end" : "flex-start",
        gap: 1,
      }}
    >
      {side === "left" ? (
        <React.Fragment>
          {names}
          {avatars}
        </React.Fragment>
      ) : (
        <React.Fragment>
          {avatars}
          {names}
        </React.Fragment>
      )}
    </Box>
  );
}

TeamSide.propTypes = {
  players: PropTypes.array,
  side: PropTypes.oneOf(["left", "right"]),
};

function ScoreValue({ value, isWinner }) {
  return (
    <Typography
      component="span"
      sx={{
        fontFamily: "'Orbitron', sans-serif",
        fontSize: 20,
        fontWeight: 800,
        lineHeight: 1,
        color: isWinner ? "#E8A33D" : "#f2f2f2",
      }}
    >
      {Number.isFinite(value) ? value : 0}
    </Typography>
  );
}

ScoreValue.propTypes = {
  value: PropTypes.number,
  isWinner: PropTypes.bool,
};

export default function LatestMatchCard({ match, serverTimeMs, isMine, hasFriends }) {
  const isLive = match.status === "live";
  const mapMeta = getMapMetaById(match.map);
  const mapImageUrl = getMapImageUrl(match.map);
  const nowMs = Number.isFinite(serverTimeMs) ? serverTimeMs : Date.now();

  const timeLabel = isLive
    ? Number.isFinite(match.startedAtMs)
      ? `Started ${formatDistance(match.startedAtMs, nowMs, { addSuffix: true })}`
      : "Live now"
    : Number.isFinite(match.finishedAtMs)
      ? formatDistance(match.finishedAtMs, nowMs, { addSuffix: true })
      : "Recently";

  return (
    <Paper
      component={RouterLink}
      to={`/match/${match.matchId}`}
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "stretch",
        gap: { xs: 1.5, sm: 2 },
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: 2,
        border: "1px solid rgba(255,255,255,0.08)",
        borderLeft: isLive
          ? "3px solid #ff5566"
          : "3px solid rgba(255,255,255,0.08)",
        textDecoration: "none",
        color: "inherit",
        transition: "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.04)",
          borderColor: "rgba(232,163,61,0.35)",
          ...(isLive ? { borderLeftColor: "#ff5566" } : {}),
        },
      }}
    >
      {/* Map thumbnail with name + region overlay */}
      <Box
        sx={{
          position: "relative",
          width: { xs: 84, sm: 132 },
          minHeight: 68,
          borderRadius: 1.5,
          flexShrink: 0,
          overflow: "hidden",
          backgroundImage: mapImageUrl ? `url(${mapImageUrl})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "rgba(255,255,255,0.06)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0.05) 100%)",
          }}
        />
        <Box sx={{ position: "absolute", left: 8, right: 8, bottom: 6 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 13,
              lineHeight: 1.15,
              color: "#ffffff",
              textShadow: "0 1px 3px rgba(0,0,0,0.7)",
            }}
          >
            {mapMeta?.display || match.map || "Unknown map"}
          </Typography>
          {match.region && (
            <Typography
              sx={{
                fontSize: 10.5,
                color: "rgba(255,255,255,0.75)",
                textShadow: "0 1px 2px rgba(0,0,0,0.7)",
              }}
            >
              {match.region}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Center: badges + head-to-head score row */}
      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.75,
        }}
      >
        {(isMine || hasFriends) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            {isMine && (
              <Chip
                label="You played"
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
              />
            )}
            {hasFriends && (
              <Chip
                label="Friends playing"
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
          <TeamSide players={match.team1} side="left" />

          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1,
            }}
          >
            <ScoreValue value={match.team1Score} isWinner={match.winnerTeam === "team1"} />
            <Typography component="span" sx={{ color: "#666", fontWeight: 700, fontSize: 13 }}>
              :
            </Typography>
            <ScoreValue value={match.team2Score} isWinner={match.winnerTeam === "team2"} />
          </Box>

          <TeamSide players={match.team2} side="right" />
        </Box>
      </Box>

      {/* Right meta column: status + time + players */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "center",
          gap: 0.5,
          minWidth: { xs: 76, sm: 96 },
        }}
      >
        {isLive ? (
          <Chip
            label="LIVE"
            size="small"
            sx={{
              height: 20,
              backgroundColor: "rgba(255, 85, 102, 0.15)",
              color: "#ff5566",
              fontWeight: 800,
              fontSize: 11,
              letterSpacing: "0.06em",
            }}
          />
        ) : (
          <Chip
            label="Final"
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: 11, color: "#999999", borderColor: "rgba(255,255,255,0.15)" }}
          />
        )}
        <Typography sx={{ fontSize: 11.5, color: "#999999", textAlign: "right" }}>
          {timeLabel}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: "#777777", textAlign: "right" }}>
          {match.playerCount} players
        </Typography>
      </Box>
    </Paper>
  );
}

LatestMatchCard.propTypes = {
  match: PropTypes.shape({
    matchId: PropTypes.string,
    map: PropTypes.string,
    status: PropTypes.oneOf(["live", "finished"]),
    region: PropTypes.string,
    team1: PropTypes.array,
    team2: PropTypes.array,
    team1Score: PropTypes.number,
    team2Score: PropTypes.number,
    winnerTeam: PropTypes.oneOf(["team1", "team2", null]),
    playerCount: PropTypes.number,
    startedAtMs: PropTypes.number,
    finishedAtMs: PropTypes.number,
  }).isRequired,
  serverTimeMs: PropTypes.number,
  isMine: PropTypes.bool,
  hasFriends: PropTypes.bool,
};
